import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, Send, X } from "lucide-react";
import { useLang } from "../../context/LangContext";
import { EASE } from "../../lib/scroll";
import { LogoMark } from "./Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getSession = () => {
  let id = sessionStorage.getItem("bv_chat_session");
  if (!id) {
    id = `bv-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem("bv_chat_session", id);
  }
  return id;
};

const Bubble = ({ m }) => {
  const me = m.role === "user";
  return (
    <div className={`flex ${me ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.role}`}>
      <div className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-3 text-sm leading-relaxed ${me ? "rounded-br-md bg-[#d4af37] text-[#0a0a0b]" : "rounded-bl-md border border-white/10 bg-white/[0.05] text-white/85"}`}>
        {m.content}{m.streaming && <span className="ml-0.5 inline-block h-3.5 w-1.5 animate-pulse bg-[#e8c979] align-middle" />}
      </div>
    </div>
  );
};

export const ChatWidget = () => {
  const { t, lang } = useLang();
  const c = t.chat;
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([]);
  const session = useRef(getSession());
  const bottom = useRef(null);

  useEffect(() => {
    if (!open) return;
    fetch(`${API}/chat/${session.current}`).then((r) => r.json()).then((h) => { if (Array.isArray(h) && h.length) setMessages(h); }).catch(() => {});
  }, [open]);

  useEffect(() => { bottom.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "", streaming: true }]);
    try {
      const res = await fetch(`${API}/chat`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_id: session.current, message: msg, lang }) });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const line = p.replace(/^data: /, "");
          if (line === "[DONE]") continue;
          const ev = JSON.parse(line);
          if (ev.error) throw new Error(ev.error);
          setMessages((m) => { const n = [...m]; n[n.length - 1] = { ...n[n.length - 1], content: n[n.length - 1].content + ev.t }; return n; });
        }
      }
      setMessages((m) => { const n = [...m]; n[n.length - 1] = { ...n[n.length - 1], streaming: false }; return n; });
    } catch {
      setMessages((m) => { const n = [...m]; n[n.length - 1] = { role: "assistant", content: c.err }; return n; });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            data-lenis-prevent
            initial={{ opacity: 0, y: 24, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="fixed bottom-[88px] right-3 z-[90] flex h-[min(560px,calc(100dvh-110px))] w-[min(400px,calc(100vw-24px))] sm:right-5 flex-col overflow-hidden rounded-[28px] border border-[#d4af37]/25 bg-[#0d0d0f]/95 text-[#fafaf7] shadow-[0_40px_120px_-30px_rgba(212,175,55,0.45)] backdrop-blur-xl"
          >
            <div className="flex items-center gap-3 border-b border-white/10 px-5 py-4">
              <LogoMark className="h-6" />
              <div className="flex-1">
                <p className="font-display text-sm font-semibold tracking-wide">{c.title}</p>
                <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em] text-white/55"><span className="h-1.5 w-1.5 rounded-full bg-[#d4af37]" />{c.status}</p>
              </div>
              <button data-testid="chat-close-button" onClick={() => setOpen(false)} aria-label={c.close} className="text-white/50 hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            <div className="flex-1 space-y-3 overflow-y-auto px-5 py-5" data-testid="chat-messages">
              <Bubble m={{ role: "assistant", content: c.welcome }} />
              {messages.map((m, i) => <Bubble key={i} m={m} />)}
              {messages.length === 0 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {c.suggestions.map((sg, i) => (
                    <button key={sg} data-testid={`chat-suggestion-${i}`} onClick={() => send(sg)} className="rounded-full border border-white/15 px-3 py-1.5 text-xs text-white/70 transition-colors hover:border-[#d4af37] hover:text-[#e8c979]">{sg}</button>
                  ))}
                </div>
              )}
              <div ref={bottom} />
            </div>
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 border-t border-white/10 p-3">
              <input
                data-testid="chat-input"
                className="field flex-1 !py-3"
                placeholder={c.placeholder}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                disabled={busy}
              />
              <button type="submit" data-testid="chat-send-button" disabled={busy || !input.trim()} aria-label={c.send} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#d4af37] text-[#0a0a0b] transition-colors hover:bg-[#e8c979] disabled:opacity-40">
                <Send className="h-4 w-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
      <motion.button
        data-testid="chat-toggle-button"
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? c.close : c.open}
        initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 3.5, duration: 0.8, ease: EASE }}
        whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.94 }}
        className="fixed bottom-4 right-3 z-[91] flex h-12 w-12 items-center justify-center rounded-full bg-[#d4af37] text-[#0a0a0b] shadow-[0_18px_50px_-12px_rgba(212,175,55,0.9)] sm:bottom-6 sm:right-5 sm:h-14 sm:w-14"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </motion.button>
    </>
  );
};
